import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { X, Loader2 } from "lucide-react";
import {
  ActionConfiguration,
  ActionNode,
  AgentNode,
  AIConfiguration,
  ManualConfiguration,
  Node,
  Edge,
  ApprovalConfig,
} from "@/store/agentsStore";
import {
  ActionDefinition,
  useAgentWorkflowActionsStore,
} from "@/store/agentWorkflowActionsStore";
import {
  TriggerDefinition,
  useAgentWorkflowTriggersStore,
} from "@/store/agentWorkflowTriggersStore";
import { JSONSchema7 } from "json-schema";
import { JSONSchemaForm } from "@/components/JSONSchemaForm";
import {
  JSONObject,
  JSONValue,
  JSONSchemaFieldTemplateProps,
  isJSONObject,
} from "@/components/jsonSchemaForm/types";
import { cn, keyToLabel } from "@/lib/utils";
import { Label } from "@/components/ui/label";
import { useMemo, useCallback } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Trash2, Bot } from "lucide-react";
import { SelectActionModal } from "./SelectActionModal";
import { useState, useEffect } from "react";
import cuid2 from "@paralleldrive/cuid2";
import { ReasoningLevel } from "@/store/agentsStore";
import { useModels } from "@/store/llmStore";
import { UserSelector } from "@/components/UserSelector";
import { ConnectionSelector } from "@/components/ConnectionSelector";
import { DynamicField } from "./DynamicField";
import { fetchApi } from "@/lib/api";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { ChevronRight } from "lucide-react";

interface NodeEditPanelProps {
  node: Node;
  isOpen: boolean;
  onClose: () => void;
  onChangeNode: (node: Node) => void;
  edges?: Edge[];
  onUpdateEdgeCondition?: (edgeId: string, condition: string) => void;
}

// Helper to get actionId or triggerId from node
const getNodeIdentifier = (
  node: Node,
): { actionId?: string; triggerId?: string } => {
  if (
    node.type === "ACTION" ||
    node.type === "CONDITION" ||
    node.type === "AGENT" ||
    node.type === "MESSAGE"
  ) {
    return { actionId: node.actionId };
  }
  if (node.type === "TRIGGER") {
    return { triggerId: node.triggerId };
  }
  return {};
};

const setNestedValue = (
  obj: JSONObject,
  path: string[],
  value: JSONValue,
): JSONObject => {
  if (path.length === 0) {
    return obj;
  }

  if (path.length === 1) {
    return { ...obj, [path[0]]: value };
  }

  const [firstKey, ...restPath] = path;
  const existingValue = obj[firstKey];
  const currentNested =
    typeof existingValue === "object" && existingValue !== null
      ? (existingValue as JSONObject)
      : {};

  return {
    ...obj,
    [firstKey]: setNestedValue(currentNested, restPath, value),
  };
};

// Selector to build node data from schema and info
const selectNodeData = (
  node: Node,
  dynamicSchema: JSONSchema7 | null,
  actionInfo: ActionDefinition | null,
  triggerInfo: TriggerDefinition | null,
): {
  title: string;
  description: string;
  fieldSchema: JSONSchema7 | null;
  manualOnly: boolean;
  fieldConfig: ActionConfiguration[];
  onChangeFieldConfig: (config: ActionConfiguration[]) => Node;
} | null => {
  const info = actionInfo || triggerInfo;
  return {
    title: info?.name || "Unknown",
    description: info?.description || "",
    fieldSchema: dynamicSchema,
    manualOnly: node.type === "TRIGGER",
    fieldConfig:
      node.type === "TRIGGER"
        ? node.triggerConfiguration
        : node.type === "ACTION" ||
            node.type === "CONDITION" ||
            node.type === "AGENT" ||
            node.type === "MESSAGE"
          ? node.actionConfiguration
          : [],
    onChangeFieldConfig: (config: ActionConfiguration[]): Node => {
      if (node.type === "TRIGGER") {
        const filteredConfig = config.filter(
          (c): c is ManualConfiguration => c.type === "MANUAL",
        );
        return {
          ...node,
          triggerConfiguration: filteredConfig,
        };
      } else {
        return {
          ...node,
          actionConfiguration: config,
        };
      }
    },
  };
};

type FormContextType = {
  configMap: Map<string, ActionConfiguration>;
  manualOnly: boolean;
  handleFieldConfigChange: (
    key: string,
    config: ActionConfiguration | null,
    shouldReloadSchema: boolean,
  ) => void;
  node: Node;
  connectionId?: string;
};

export function ExtendedJSONSchemaFieldTemplate({
  id,
  label,
  description,
  required,
  error,
  children,
  className,
  schemaPath,
  formContext,
  schema,
  currValue,
}: JSONSchemaFieldTemplateProps<FormContextType>) {
  const fieldKey = schemaPath.join(".");
  const config = formContext?.configMap.get(fieldKey);

  const isDynamicField = schema.isDynamic;
  const isCronTimerField = schema.isCronTimer;
  const isManualOnlyField = schema.isManualOnly;
  const shouldReloadSchema = schema.shouldReload ?? false;

  // Determine the current field mode
  const getFieldMode = (): "manual" | "ai" | "ai-prompt" => {
    if (!config) {
      return "ai";
    }

    if (config.type === "MANUAL") {
      return "manual";
    }

    if (config.type === "AI") {
      return "ai-prompt";
    }
    return "ai";
  };

  const currentMode = getFieldMode();

  const handleModeChange = (mode: string) => {
    if (!formContext?.handleFieldConfigChange) {
      return;
    }

    if (mode === "manual") {
      formContext.handleFieldConfigChange(
        fieldKey,
        {
          type: "MANUAL",
          input:
            (config?.type === "MANUAL" ? config.input : undefined) || undefined,
          schemaPath: schemaPath,
        },
        shouldReloadSchema,
      );
      return;
    }

    if (mode === "ai") {
      formContext.handleFieldConfigChange(fieldKey, null, shouldReloadSchema);
      return;
    }

    if (mode === "ai-prompt") {
      formContext.handleFieldConfigChange(
        fieldKey,
        {
          type: "AI",
          prompt: (config?.type === "AI" ? config.prompt : "") || "",
          schemaPath: schemaPath,
        },
        shouldReloadSchema,
      );
      return;
    }
  };

  const handlePromptChange = (prompt: string) => {
    if (!formContext?.handleFieldConfigChange) {
      return;
    }

    const newConfig: AIConfiguration = {
      type: "AI",
      prompt: prompt,
      schemaPath: schemaPath,
    };

    formContext.handleFieldConfigChange(
      fieldKey,
      newConfig,
      shouldReloadSchema,
    );
  };

  // Handle dynamic fields
  if (isDynamicField && formContext) {
    const handleDynamicFieldChange = (option: {
      input: JSONValue;
      label?: string;
    }) => {
      if (!formContext?.handleFieldConfigChange) {
        return;
      }
      // for arrays, we need to update the array directly
      // so this check if this field is within an array
      const isChildField = schemaPath.length > 1;
      if (isChildField) {
        const parentPath = schemaPath.slice(0, -1);
        const parentKey = parentPath.join(".");
        const parentConfig = formContext.configMap.get(parentKey);

        const isManualArrayField =
          parentConfig?.type === "MANUAL" &&
          parentConfig.input &&
          Array.isArray(parentConfig.input);

        if (isManualArrayField && Array.isArray(parentConfig.input)) {
          // update the array directly
          const duplicatedArray = [...parentConfig.input];
          const indexToUpdate = Number(schemaPath[schemaPath.length - 1]);
          duplicatedArray[indexToUpdate] = option.input;
          const newParentConfig = { ...parentConfig, input: duplicatedArray };

          formContext.handleFieldConfigChange(
            parentKey,
            newParentConfig,
            shouldReloadSchema,
          );
          return;
        }
      }

      const newConfig: ManualConfiguration = {
        type: "MANUAL",
        input: option.input,
        label: option.label,
        schemaPath: schemaPath,
      };

      formContext.handleFieldConfigChange(
        fieldKey,
        newConfig,
        shouldReloadSchema,
      );
    };

    const configuration =
      formContext.node.type === "ACTION"
        ? formContext.node.actionConfiguration
        : formContext.node.type === "TRIGGER"
          ? formContext.node.triggerConfiguration
          : [];

    const shouldAllowAI =
      !shouldReloadSchema &&
      !(
        formContext?.manualOnly ||
        schemaPath.length > 1 ||
        isCronTimerField ||
        isManualOnlyField
      );

    return (
      <div className={cn("space-y-2", className)}>
        {label && (
          <div className="flex items-center justify-between">
            <Label htmlFor={id}>
              {keyToLabel(label)}
              {required && <span className="text-destructive ml-1">*</span>}
            </Label>
            {/* We allow AI to set fields that don't require dynamic reloads */}
            {shouldAllowAI && (
              <Select value={currentMode} onValueChange={handleModeChange}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Select mode" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="manual">Set Manually</SelectItem>
                  <SelectItem value="ai">Auto</SelectItem>
                  <SelectItem value="ai-prompt">Prompt AI</SelectItem>
                </SelectContent>
              </Select>
            )}
          </div>
        )}
        {description && (
          <p className="text-sm text-muted-foreground">{description}</p>
        )}
        {shouldAllowAI && currentMode === "ai-prompt" && (
          <Textarea
            placeholder="Enter prompt for AI to use when filling this field..."
            value={config?.type === "AI" ? config.prompt : ""}
            onChange={(e) => handlePromptChange(e.target.value)}
            className="min-h-[80px]"
          />
        )}

        {(!shouldAllowAI || currentMode === "manual") && (
          <DynamicField
            actionId={
              formContext.node.type === "ACTION"
                ? formContext.node.actionId
                : undefined
            }
            triggerId={
              formContext.node.type === "TRIGGER"
                ? formContext.node.triggerId
                : undefined
            }
            // dynamic field values are stored at the top level of the schema for a given field
            fieldName={schemaPath[0]}
            value={currValue}
            existingFieldConfiguration={configuration}
            connectionId={formContext.connectionId}
            onChange={handleDynamicFieldChange}
          />
        )}

        {shouldAllowAI && currentMode === "ai" && (
          <p className="text-sm text-muted-foreground italic">
            AI will automatically fill this field based on the previous steps.
          </p>
        )}

        {error && currentMode === "manual" && (
          <p className="text-sm text-destructive">{error}</p>
        )}
      </div>
    );
  }

  // if it is a nested value - dont allow using AI on the individual value
  if (
    formContext?.manualOnly ||
    schemaPath.length > 1 ||
    isCronTimerField ||
    isManualOnlyField
  ) {
    return (
      <div className={cn("space-y-2", className)}>
        {label && (
          <Label htmlFor={id}>
            {keyToLabel(label)}
            {required && <span className="text-destructive ml-1">*</span>}
          </Label>
        )}
        {description && (
          <p className="text-sm text-muted-foreground">{description}</p>
        )}
        {children}
        {error && <p className="text-sm text-destructive">{error}</p>}
      </div>
    );
  }

  return (
    <div className={cn("space-y-2", className)}>
      <div className="flex items-center justify-between">
        {label && (
          <Label htmlFor={id} className="first-letter:capitalize">
            {keyToLabel(label)}
            {required && <span className="text-destructive ml-1">*</span>}
          </Label>
        )}
        <Select value={currentMode} onValueChange={handleModeChange}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Select mode" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="manual">Set Manually</SelectItem>
            <SelectItem value="ai">Auto</SelectItem>
            <SelectItem value="ai-prompt">Prompt AI</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {description && (
        <p className="text-sm text-muted-foreground">{description}</p>
      )}

      {currentMode === "ai-prompt" && (
        <Textarea
          placeholder="Enter prompt for AI to use when filling this field..."
          value={config?.type === "AI" ? config.prompt : ""}
          onChange={(e) => handlePromptChange(e.target.value)}
          className="min-h-[80px]"
        />
      )}

      {currentMode === "manual" && children}

      {currentMode === "ai" && (
        <p className="text-sm text-muted-foreground italic">
          AI will automatically fill this field based on the previous steps.
        </p>
      )}

      {error && currentMode === "manual" && (
        <p className="text-sm text-destructive first-letter:capitalize">
          {error}
        </p>
      )}
    </div>
  );
}

export function NodeEditPanel({
  node,
  isOpen,
  onClose,
  onChangeNode,
  edges = [],
  onUpdateEdgeCondition,
}: NodeEditPanelProps) {
  const { getActionById } = useAgentWorkflowActionsStore();
  const { getTriggerById } = useAgentWorkflowTriggersStore();
  const [showActionSelector, setShowActionSelector] = useState(false);
  const { models, isLoading: modelsLoading, fetchModels } = useModels();
  const [dynamicSchema, setDynamicSchema] = useState<JSONSchema7 | null>(null);
  const [isLoadingSchema, setIsLoadingSchema] = useState(false);
  const [schemaError, setSchemaError] = useState<string | null>(null);
  const [actionInfo, setActionInfo] = useState<ActionDefinition | null>(null);
  const [triggerInfo, setTriggerInfo] = useState<TriggerDefinition | null>(
    null,
  );
  const [selectedSkillIdForEdit, setSelectedSkillIdForEdit] = useState<
    string | null
  >(null);

  const selectedSkillForEdit = useMemo(
    () =>
      node.type === "AGENT"
        ? node.actions.find((skill) => skill.id === selectedSkillIdForEdit)
        : null,
    [node, selectedSkillIdForEdit],
  );

  const currentConfig = useMemo(
    () =>
      node.type === "TRIGGER"
        ? node.triggerConfiguration
        : node.type === "ACTION" ||
            node.type === "CONDITION" ||
            node.type === "AGENT" ||
            node.type === "MESSAGE"
          ? node.actionConfiguration
          : [],
    [node],
  );

  // Fetch models on mount if not already loaded
  useEffect(() => {
    fetchModels();
  }, [fetchModels]);

  // Fetch action/trigger info and schema when panel opens
  const fetchNodeInfo = useCallback(
    async (currentConfig: ActionConfiguration[]) => {
      if (!isOpen) {
        return;
      }

      setIsLoadingSchema(true);
      setSchemaError(null);

      try {
        let currentActionInfo: ActionDefinition | null = null;
        let currentTriggerInfo: TriggerDefinition | null = null;

        // Get action or trigger info from store
        const { actionId, triggerId } = getNodeIdentifier(node);

        if (actionId) {
          currentActionInfo = getActionById(actionId) || null;
          setActionInfo(currentActionInfo);
        } else if (triggerId) {
          currentTriggerInfo = getTriggerById(triggerId) || null;
          setTriggerInfo(currentTriggerInfo);
        }

        // Get the input schema with existing configuration
        const schemaResponse = await fetchApi("apps/input-schema", "POST", {
          body: {
            nodeId: node.id,
            actionId: actionId,
            triggerId: triggerId,
            existingFieldConfiguration: currentConfig,
            connectionId: node.connectionConfig?.connectionId
              ? node.connectionConfig.connectionId
              : undefined,
          },
        });

        if (schemaResponse.ok && schemaResponse.data) {
          const data = schemaResponse.data as { schema: JSONSchema7 };
          setDynamicSchema(data.schema);
        } else {
          setSchemaError("Failed to fetch input schema");
        }
      } catch (error) {
        setSchemaError(
          error instanceof Error
            ? error.message
            : "Failed to fetch node information",
        );
      } finally {
        setIsLoadingSchema(false);
      }
    },
    [isOpen, node, getActionById, getTriggerById],
  );

  useEffect(() => {
    fetchNodeInfo(currentConfig);
  }, [node.id]);

  // Handle connection change - refetch schema
  const handleConnectionChange = useCallback(
    async (connectionId: string) => {
      // Update the node with the new connectionId
      const updatedNode: Node = {
        ...node,
        connectionConfig: { connectionId },
      };
      onChangeNode(updatedNode);
    },
    [node, onChangeNode],
  );

  useEffect(() => {
    // refresh the schema when the connection changes
    fetchNodeInfo(currentConfig);
  }, [node.connectionConfig?.connectionId]);

  // Use dynamic schema with selector
  const nodeData = useMemo(
    () => selectNodeData(node, dynamicSchema, actionInfo, triggerInfo),
    [node, dynamicSchema, actionInfo, triggerInfo],
  );
  // Get outgoing edges for condition and agent nodes
  const outgoingEdges = useMemo(() => {
    if (node.type === "CONDITION" || node.type === "AGENT") {
      return edges.filter((edge) => edge.from === node.id);
    }
    return [];
  }, [node, edges]);

  const fieldConfig = useMemo(() => nodeData?.fieldConfig || [], [nodeData]);
  const onChangeFieldConfig = useMemo(
    () => nodeData?.onChangeFieldConfig || (() => node),
    [nodeData, node],
  );
  const manualOnly = useMemo(() => nodeData?.manualOnly || false, [nodeData]);

  const configMap = useMemo(() => {
    const configMapBuilder = new Map<string, ActionConfiguration>();
    fieldConfig.forEach((config) => {
      const key = config.schemaPath.join(".");
      configMapBuilder.set(key, config);
    });
    return configMapBuilder;
  }, [fieldConfig]);

  const handleFieldConfigChange = useCallback(
    (
      key: string,
      config: ActionConfiguration | null,
      shouldReloadSchema: boolean,
    ) => {
      const otherConfigs = fieldConfig.filter(
        (c) => c.schemaPath.join(".") !== key,
      );

      if (config === null) {
        const updatedNode = onChangeFieldConfig(otherConfigs);
        onChangeNode(updatedNode);
        if (shouldReloadSchema) {
          fetchNodeInfo(otherConfigs);
        }
        return;
      }

      const newFieldConfig = [...otherConfigs, config];
      const updatedNode = onChangeFieldConfig(newFieldConfig);
      onChangeNode(updatedNode);

      if (shouldReloadSchema) {
        fetchNodeInfo(newFieldConfig);
      }
    },
    [fieldConfig, onChangeFieldConfig, onChangeNode],
  );

  const formContext = useMemo(
    () => ({
      configMap,
      manualOnly,
      handleFieldConfigChange,
      node,
      connectionId: node.connectionConfig
        ? node.connectionConfig.connectionId
        : undefined,
      onRefreshSchema: fetchNodeInfo,
    }),
    [configMap, manualOnly, handleFieldConfigChange, node, fetchNodeInfo],
  );

  const handleFormChange = useCallback(
    (data: JSONValue, shouldReloadSchema: boolean) => {
      if (!isJSONObject(data)) {
        return;
      }

      // loop through the data and build a new fieldConfig
      const newManualConfig: ActionConfiguration[] = [];
      const buildManualConfigFromObject = (obj: JSONObject, path: string[]) => {
        for (const [key, value] of Object.entries(obj)) {
          if (value === null) {
            newManualConfig.push({
              type: "MANUAL",
              input: null,
              schemaPath: [...path, key],
            });
            continue;
          }

          if (Array.isArray(value)) {
            newManualConfig.push({
              type: "MANUAL",
              input: value,
              schemaPath: [...path, key],
            });
            continue;
          }

          if (typeof value === "object") {
            buildManualConfigFromObject(value, [...path, key]);
            continue;
          }

          newManualConfig.push({
            type: "MANUAL",
            input: value,
            schemaPath: [...path, key],
          });
        }
      };
      buildManualConfigFromObject(data, []);
      const aiConfigValues = fieldConfig.filter((c) => c.type === "AI");
      const newConfig = [...aiConfigValues, ...newManualConfig];
      const updatedNode = onChangeFieldConfig(newConfig);
      onChangeNode(updatedNode);

      if (shouldReloadSchema) {
        fetchNodeInfo(newConfig);
      }
    },
    [fieldConfig, onChangeFieldConfig, onChangeNode],
  );

  const formData = useMemo(() => {
    let formDataBuilder: JSONObject = {};
    fieldConfig.forEach((config) => {
      if (config.type === "MANUAL") {
        formDataBuilder = setNestedValue(
          formDataBuilder,
          config.schemaPath,
          config.input,
        );
      }
    });
    return formDataBuilder;
  }, [fieldConfig]);

  // Agent-specific handlers
  const handlePromptChange = useCallback(
    (prompt: string) => {
      if (node.type === "AGENT") {
        const updatedNode: AgentNode = {
          ...node,
          prompt,
        };
        onChangeNode(updatedNode);
      }
    },
    [node, onChangeNode],
  );

  const handleShouldSummarizeChange = useCallback(
    (checked: boolean) => {
      if (node.type === "AGENT") {
        const updatedNode: AgentNode = {
          ...node,
          shouldSummarize: checked,
        };
        onChangeNode(updatedNode);
      }
    },
    [node, onChangeNode],
  );

  const handleAddSkill = useCallback(
    (action: ActionDefinition) => {
      if (
        node.type === "AGENT" &&
        (action.type === "ACTION" || action.type === "MESSAGE")
      ) {
        const newSkill: ActionNode = {
          id: cuid2.createId(),
          type: action.type,
          actionId: action.id,
          actionConfiguration: [],
          position: { x: 0, y: 0 },
        };
        const updatedNode: AgentNode = {
          ...node,
          actions: [...(node.actions || []), newSkill],
        };
        onChangeNode(updatedNode);
      }
    },
    [node, onChangeNode],
  );

  const handleRemoveSkill = useCallback(
    (skillId: string) => {
      if (node.type === "AGENT") {
        const updatedNode: AgentNode = {
          ...node,
          actions: node.actions.filter((skill) => skill.id !== skillId),
        };
        onChangeNode(updatedNode);
      }
    },
    [node, onChangeNode],
  );

  const handleUpdateSkill = useCallback(
    (updatedSkill: Node) => {
      if (
        node.type === "AGENT" &&
        (updatedSkill.type === "ACTION" || updatedSkill.type === "MESSAGE")
      ) {
        const updatedNode: AgentNode = {
          ...node,
          actions: node.actions.map((skill) =>
            skill.id === updatedSkill.id ? updatedSkill : skill,
          ),
        };
        onChangeNode(updatedNode);
      }
    },
    [node, onChangeNode],
  );

  const handleModelChange = useCallback(
    (model: string | undefined) => {
      if (
        node.type === "AGENT" ||
        node.type === "ACTION" ||
        node.type === "MESSAGE"
      ) {
        if (model === undefined) {
          // Remove modelConfig if "Use Best Available Model" is selected
          const { modelConfig: _, ...nodeWithoutModel } = node;
          onChangeNode(nodeWithoutModel);
        } else {
          const selectedModel = models.find((m) => m.model === model);
          const updatedNode = {
            ...node,
            modelConfig: {
              model,
              reasoningLevel: selectedModel?.isReasoningModel
                ? ("medium" as const)
                : undefined,
            },
          };
          onChangeNode(updatedNode);
        }
      }
    },
    [node, onChangeNode, models],
  );

  const handleReasoningLevelChange = useCallback(
    (level: ReasoningLevel) => {
      if (
        (node.type === "AGENT" ||
          node.type === "ACTION" ||
          node.type === "MESSAGE") &&
        node.modelConfig
      ) {
        const updatedNode = {
          ...node,
          modelConfig: {
            ...node.modelConfig,
            reasoningLevel: level,
          },
        };
        onChangeNode(updatedNode);
      }
    },
    [node, onChangeNode],
  );

  const handleApprovalConfigChange = useCallback(
    (userIds: string[]) => {
      if (node.type === "ACTION") {
        if (userIds.length === 0) {
          // Clear approvalConfig when no users are selected
          const { approvalConfig: _, ...nodeWithoutApproval } = node;
          onChangeNode(nodeWithoutApproval);
        } else {
          // Update approvalConfig with selected users
          const approvalConfig: ApprovalConfig = {
            approvers: userIds.map((userId) => ({
              type: "USER",
              userId,
            })),
          };
          const updatedNode: ActionNode = {
            ...node,
            approvalConfig,
          };
          onChangeNode(updatedNode);
        }
      }
    },
    [node, onChangeNode],
  );

  const isAgent = node.type === "AGENT";
  const isCondition = node.type === "CONDITION";

  // Determine if connection is required from action/trigger info
  const requiresConnection =
    actionInfo?.appConnectionConfig || triggerInfo?.appConnectionConfig;
  const appId =
    actionInfo?.appConnectionConfig?.appId ||
    triggerInfo?.appConnectionConfig?.appId;
  const providerId =
    actionInfo?.appConnectionConfig?.providerId ||
    triggerInfo?.appConnectionConfig?.providerId;

  return (
    <Sheet open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <SheetContent
        side="right"
        className="w-full sm:w-[480px] sm:max-w-[480px] overflow-y-auto p-4 sm:p-6"
      >
        <SheetHeader className="mb-4 sm:mb-6">
          <div className="flex items-center justify-between gap-2">
            <SheetTitle className="text-base sm:text-lg truncate pr-2">
              {nodeData?.title}
            </SheetTitle>
            <Button
              variant="ghost"
              size="icon"
              className="h-9 w-9 sm:h-8 sm:w-8 flex-shrink-0"
              onClick={onClose}
            >
              <X className="h-5 w-5 sm:h-4 sm:w-4" />
            </Button>
          </div>
          {nodeData?.description && (
            <p className="text-sm text-muted-foreground mt-2 line-clamp-3 sm:line-clamp-none">
              {nodeData.description}
            </p>
          )}
        </SheetHeader>

        {/* Connection selector for nodes that support it */}
        {requiresConnection && appId && providerId && (
          <div className="space-y-2 mb-4 sm:mb-6">
            <Label>App Connection</Label>
            <ConnectionSelector
              appId={appId}
              providerId={providerId}
              connectionId={node.connectionConfig?.connectionId}
              onSelect={handleConnectionChange}
            />
          </div>
        )}

        {/* Model selection for ACTION nodes */}
        {(node.type === "ACTION" ||
          node.type === "AGENT" ||
          node.type === "MESSAGE") && (
          <div className="space-y-2 mb-4 sm:mb-6">
            <Label>Model</Label>
            <Select
              value={node.modelConfig?.model || "default"}
              onValueChange={(value) =>
                handleModelChange(value === "default" ? undefined : value)
              }
              disabled={modelsLoading}
            >
              <SelectTrigger className="h-12 sm:h-14">
                <SelectValue placeholder="Select a model" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="default">
                  Use Best Available Model
                </SelectItem>
                {models.map((model) => (
                  <SelectItem key={model.model} value={model.model}>
                    <div className="flex flex-col w-full align-start text-start">
                      <div className="font-medium">{model.name}</div>
                      <div className="text-xs text-muted-foreground">
                        {model.description}
                      </div>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {(node.type === "ACTION" ||
              node.type === "AGENT" ||
              node.type === "MESSAGE") &&
              node.modelConfig &&
              models.find((m) => m.model === node.modelConfig?.model)
                ?.isReasoningModel && (
                <div className="space-y-2">
                  <Label>Reasoning Level</Label>
                  <Select
                    value={node.modelConfig.reasoningLevel || "medium"}
                    onValueChange={(value) =>
                      handleReasoningLevelChange(value as ReasoningLevel)
                    }
                  >
                    <SelectTrigger className="h-10">
                      <SelectValue placeholder="Select reasoning level" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
          </div>
        )}

        {/* Approval configuration for ACTION nodes */}
        {node.type === "ACTION" && (
          <div className="space-y-2 mb-4 sm:mb-6">
            <Label>Approval Required From</Label>
            <UserSelector
              mode="multi"
              selectedUserIds={
                node.approvalConfig?.approvers.map(
                  (approver) => approver.userId,
                ) || []
              }
              onUserIdsChange={handleApprovalConfigChange}
              placeholder="Select users who must approve..."
            />
            <p className="text-xs text-muted-foreground">
              When selected, this action will be routed to all of the selected
              users for approval. One must approve before the action will
              execute.
            </p>
          </div>
        )}

        {/* Show prompt field for AGENT nodes */}
        {isAgent && (
          <div className="space-y-4 mb-4 sm:mb-6">
            <div className="space-y-2">
              <Label>Prompt</Label>
              <Textarea
                value={(node.type === "AGENT" && node.prompt) || ""}
                onChange={(e) => handlePromptChange(e.target.value)}
                placeholder="Provide instructions for the agent to follow..."
                className="min-h-[100px] text-sm"
              />
              <p className="text-xs text-muted-foreground">
                Give the agent specific instructions about how to approach tasks
              </p>
            </div>

            {/* Skills list */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Skills</Label>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowActionSelector(true)}
                  className="h-9 sm:h-8"
                >
                  <Plus className="w-3 h-3 mr-1" />
                  Add Skill
                </Button>
              </div>
              {node.type === "AGENT" &&
              node.actions &&
              node.actions.length > 0 ? (
                <div className="space-y-2">
                  {node.actions.map((skill) => {
                    const skillAction = getActionById(skill.actionId);
                    return (
                      <div
                        key={skill.id}
                        className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 cursor-pointer transition-colors active:bg-muted/70"
                        onClick={() => setSelectedSkillIdForEdit(skill.id)}
                      >
                        <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
                          {skillAction?.imageUrl ? (
                            <img
                              src={skillAction.imageUrl}
                              alt={skillAction.name}
                              className="w-6 h-6 rounded flex-shrink-0"
                            />
                          ) : (
                            <div className="w-6 h-6 bg-gray-100 rounded flex items-center justify-center flex-shrink-0">
                              <Bot className="w-4 h-4 text-gray-500" />
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium truncate">
                              {skillAction?.name || "Unknown Action"}
                            </div>
                            {skillAction?.description && (
                              <div className="text-xs text-muted-foreground line-clamp-1">
                                {skillAction.description}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-9 w-9 sm:h-8 sm:w-8"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemoveSkill(skill.id);
                            }}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                          <ChevronRight className="w-4 h-4 text-muted-foreground" />
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-sm text-muted-foreground p-4 border border-dashed rounded-lg">
                  No skills added yet. Add skills for the agent to use.
                </div>
              )}
            </div>

            {/* Summarization option */}
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="should-summarize"
                  checked={node.shouldSummarize}
                  onCheckedChange={handleShouldSummarizeChange}
                  className="h-5 w-5 sm:h-4 sm:w-4"
                />
                <Label
                  htmlFor="should-summarize"
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                >
                  Summarize agent execution
                </Label>
              </div>
              <p className="text-xs text-muted-foreground ml-7 sm:ml-6">
                When enabled, the agent's tool calls will be summarized for
                better context management in subsequent workflow steps
              </p>
            </div>
          </div>
        )}

        {/* Show condition edges for CONDITION nodes */}
        {(isCondition || isAgent) && (
          <div className="space-y-4 mb-4 sm:mb-6">
            <Label>{isAgent ? "Exit Conditions" : "Conditions"}</Label>
            {outgoingEdges.length === 0 ? (
              <div className="text-sm text-muted-foreground p-4 border border-dashed rounded-lg">
                No outgoing connections. Connect this node to other nodes to
                configure conditions.
              </div>
            ) : (
              <div className="space-y-3">
                {outgoingEdges.map((edge, index) => (
                  <div key={edge.id} className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Label className="text-sm font-medium">
                        {isAgent ? "Exit Condition" : "Condition"} {index + 1}
                      </Label>
                    </div>
                    <Textarea
                      value={edge.condition || ""}
                      onChange={(e) => {
                        if (onUpdateEdgeCondition) {
                          onUpdateEdgeCondition(edge.id, e.target.value);
                        }
                      }}
                      placeholder={
                        isAgent
                          ? "Exit once E.g. 'the task is complete'"
                          : "Go down this path if E.g. 'the customer asks to speak to a human'"
                      }
                      className="min-h-[80px] text-sm"
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Show error if schema fetch failed */}
        {schemaError && !isLoadingSchema && (
          <Alert variant="destructive" className="mb-4 sm:mb-6">
            <AlertDescription>{schemaError}</AlertDescription>
          </Alert>
        )}

        {/* Show regular form for non-condition nodes or if condition node has a schema */}
        {(!requiresConnection ||
          (requiresConnection && node.connectionConfig?.connectionId)) &&
          !isCondition &&
          !isAgent &&
          nodeData?.fieldSchema && (
            <>
              <JSONSchemaForm<FormContextType>
                schema={nodeData.fieldSchema}
                onChange={handleFormChange}
                formData={formData}
                formContext={formContext}
                fieldTemplate={ExtendedJSONSchemaFieldTemplate}
              />

              {!nodeData?.fieldSchema &&
                !isCondition &&
                !isAgent &&
                !isLoadingSchema && (
                  <div className="text-sm text-muted-foreground">
                    No configuration schema available for this node.
                  </div>
                )}
            </>
          )}
        {/* Show loading state while fetching schema */}
        {isLoadingSchema && (
          <div className="flex items-center justify-center p-8">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        )}
      </SheetContent>

      {/* Action selector modal for adding skills */}
      {isAgent && (
        <SelectActionModal
          open={showActionSelector}
          onOpenChange={setShowActionSelector}
          onSelectAction={handleAddSkill}
          filterTypes={["ACTION", "MESSAGE"]}
        />
      )}

      {/* Nested NodeEditPanel for editing selected skill */}
      {selectedSkillForEdit && node.type === "AGENT" && (
        <NodeEditPanel
          node={selectedSkillForEdit}
          isOpen={!!selectedSkillForEdit}
          onClose={() => setSelectedSkillIdForEdit(null)}
          onChangeNode={handleUpdateSkill}
        />
      )}
    </Sheet>
  );
}
